<template>
  <b-modal
    v-model="editorShown"
    :title="formGroup ? formGroup.name : ''"
    @ok="submitForm"
    centered
    size="md"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="p-0 d-block shadow-form"
    footer-class="bg-extra-light-gray shadow-form"
  >
    <template v-slot:modal-ok="{}">Save</template>
    <template v-slot:modal-cancel="{}">Cancel</template>
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center bg-purple"
        style="padding: 13px 20px;"
      >
        <div>
          <h5 class="modal-title font-weight-bold">{{ modalTitle }}</h5>
          <div class="modal-subtitle">{{ modalSubtitle }}</div>
        </div>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity:1 !important"
          @click="closeModal"
        >
          ×
        </button>
      </div>
      <div
        class="w-100 bg-extra-light-gray d-flex flex-column justify-content-between align-items-center"
        style="padding: 8px 20px;"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <b-input-group style="width: 278px">
            <effective-date-time-picker
              :disabled="submit && submit.status == 'Completed'"
              v-model="effectiveDateTime"
              :errors="effectiveDateTimeErrors"
            />
          </b-input-group>
        </div>
        <option-switch v-model="status" :options="statuses" />
      </div>
    </template>
    <template v-slot:modal-footer>
      <div
        class="d-flex flex-row justify-content-between align-items-center w-100"
      >
        <div class="mt-1">
          <div
            class="d-flex align-items-center mr-2 color-custom-dark-grey"
            v-if="submit && submit.lastUpdated"
          >
            <i class="fa fa-save mr-2 text-primary"></i
            ><span style="font-size: 12px"
              >Last saved: {{ formatDate(submit.lastUpdated) }}
              <span class="text-primary"
                >({{ formatTimeAgo(submit.lastUpdated) }})</span
              ></span
            >
          </div>
        </div>
        <div class="text-danger text-left">{{ validationErrorMessage }}</div>
        <div class="mt-1">
          <b-button @click="closeModal" variant="secondary" class="mr-2"
            >Close</b-button
          >
          <b-button
            v-if="
              status == 'Completed' &&
                (!submit || (submit && submit.status != 'Completed'))
            "
            variant="success"
            @click="lockDocument"
          >
            Complete & Lock
          </b-button>
          <b-button
            v-else-if="
              status == 'Completed' && submit && submit.status == 'Completed'
            "
            variant="danger"
            :disabled="
              !hasCurrentUserDocumentPermission(formId, 'hasCompleteAccess')
            "
            @click="showUnlockDocument = true"
          >
            <i class="fa fa-unlock mr-2" aria-hidden="true"></i>Unlock
          </b-button>
          <b-button v-else variant="primary" @click="prevSubmitForm()">
            Save
          </b-button>
        </div>
      </div>
    </template>
    <b-row>
      <b-col>
        <div class="text-left">
          <div class="overlay" :class="showOverlay ? 'show' : ''">
            <div class="d-flex flex-column align-items-center ">
              <img width="200" :src="showOverlay ? checkGif : ''" />
              <span style="color: #2fd0f4; font-size: 20px; margin-top: -16px;"
                >Document completed
              </span>
              <span style="color: #27d58e; font-size: 24px; font-weight: bold;"
                >Well done!!</span
              >
            </div>
          </div>
          <div class="builder w-100">
            <b-row>
              <b-col>
                <b-form-group
                  description=""
                  label="Document"
                  :state="fileErrors.length == 0 ? null : false"
                  :invalid-feedback="fileErrors.join(', ')"
                >
                  <div v-if="prevFile">
                    <span>{{ prevFile }}</span
                    ><i
                      v-if="submit && submit.status != 'Completed'"
                      class="fa fa-close text-danger ml-2 hover-pointer"
                      @click="prevFile = ''"
                    ></i>
                  </div>
                  <b-form-file
                    v-else
                    v-model="file"
                    ref="fileUpload"
                  ></b-form-file>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
    <dialog-unlock-document
      v-if="showUnlockDocument"
      v-model="showUnlockDocument"
      @confirm="unlockDocument"
      @cancel="showUnlockDocument = false"
    />
  </b-modal>
</template>

<script>
import { mapGetters, mapMutations, mapState, mapActions } from "vuex";
import { formatDateTimeSql, formatDateTimeMysql } from "../../../helpers/date";
import {
  validateRequired,
  validateDateTime
} from "../../../helpers/validation";
import { hasClass } from "../../../helpers/layout";
import OptionSwitch from "../../controls/OptionSwitch.vue";
import EffectiveDateTimePicker from "../../controls/EffectiveDateTimePicker";
import DialogUnlockDocument from "../../controls/DialogUnlockDocument";
import serverEoc from "../../../server/eoc";

export default {
  name: "DocumentGroupFileAddEditModal",
  components: { EffectiveDateTimePicker, OptionSwitch, DialogUnlockDocument },
  props: ["formGroupId", "clientId", "eocId", "submitId"],
  data() {
    return {
      formGroup: null,
      file: null,
      submit: null,
      validationErrorMessage: "",
      prevFile: "",
      status: "Pending",
      effectiveDateTime: formatDateTimeMysql(),
      fileErrors: [],
      effectiveDateTimeErrors: [],
      eoc: null,
      refetchDocuments: false,
      showOverlay: false,
      showUnlockDocument: false,
      checkGif: require("../../../assets/simple-check.gif")
    };
  },
  mounted() {
    this.effectiveDateTime = formatDateTimeMysql(
      this.getCurrentAdjustedDateTime()
    );
    this.initializeFormGroupSubmitter();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapMutations("formSubmitter", [
      "initializeFormSubmitterForm",
      "setInitialValues",
      "setFormSubmitterFormHasUnsavedChanges"
    ]),
    ...mapActions("formSubmitter", [
      "addFormGroupSubmitterFile",
      "updateFormGroupSubmitterFile"
    ]),
    async initializeFormGroupSubmitter() {
      this.formGroup = await this.getFormGroupById(this.formGroupId);
      if (this.submitId) {
        this.submit = await this.getFormGroupSubmitById(this.submitId);
        this.effectiveDateTime = formatDateTimeMysql(this.submit.effectiveDate);
        this.prevFile = this.submit.fileName;
        this.status = this.submit.status;
      }
      this.eoc = await serverEoc.get(this.eocId);
      this.setFormSubmitterFormHasUnsavedChanges({ hasUnsavedChanges: false });
    },
    validateSubmit() {
      let withoutErrors = true;

      this.fileErrors = [];

      if (!validateRequired(this.file) && !validateRequired(this.prevFile)) {
        this.fileErrors.push("This field is required");
        withoutErrors = false;
      }

      if (this.file) {
        const file = this.$refs.fileUpload.files[0];
        const fileSize = (file.size / 1024 / 1024).toFixed(4);

        if (fileSize > 100) {
          this.fileErrors.push("File max size 100 mb");
          withoutErrors = false;
        }
      }

      this.effectiveDateTimeErrors = [];
      if (!validateRequired(this.effectiveDateTime)) {
        this.effectiveDateTimeErrors.push("This field is required");
        withoutErrors = false;
      }
      if (this.effectiveDateTime && !validateDateTime(this.effectiveDateTime)) {
        this.effectiveDateTimeErrors.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    prevSubmitForm(closeAfter = false) {
      if (
        this.submit &&
        this.submit.status == "Completed" &&
        this.status != this.submit.status
      ) {
        this.$dialog
          .confirm(
            {
              title:
                'This will set the document status to "PENDING" do you want to continue?'
            },
            {
              view: "confirm"
            }
          )
          .then(async () => {
            this.submitForm(closeAfter);
          })
          .catch(function() {});
      } else {
        this.submitForm(closeAfter);
      }
    },
    async lockDocument() {
      const result = await this.validateSubmit();
      if (result) {
        this.showOverlay = true;
        setTimeout(() => {
          this.showOverlay = false;
          this.submitForm(true);
        }, 1800);
      } else {
        this.scrolltoErrors();
      }
    },
    scrolltoErrors() {
      let firstErrorElement = document.querySelector(".is-invalid");
      let topPos = 0;
      while (!hasClass(firstErrorElement, "modal-body")) {
        topPos += firstErrorElement.offsetTop;
        firstErrorElement = firstErrorElement.offsetParent;
      }
      document.querySelector(".submit-modal .modal-body").scrollTop =
        topPos - 10;
      this.validationErrorMessage = "Please fix errors and try again.";
    },
    unlockDocument({ reason }) {
      this.status = "Pending";
      this.showUnlockDocument = false;
      this.submitForm(false, reason);
    },
    async submitForm(closeAfter, unlockReason = null) {
      this.validationErrorMessage = "";
      const result = this.validateSubmit();
      if (result) {
        if (this.submitId) {
          await this.updateFormGroupSubmitterFile({
            submitId: this.submitId,
            file: this.file,
            formGroupId: this.formGroupId,
            date: formatDateTimeSql(this.effectiveDateTime),
            status: this.status,
            unlockReason: unlockReason
          });
        } else {
          const id = await this.addFormGroupSubmitterFile({
            file: this.file,
            eocId: this.eocId,
            clientId: this.clientId,
            formGroupId: this.formGroupId,
            date: formatDateTimeSql(this.effectiveDateTime),
            status: this.status
          });

          this.$emit("update:submitId", id);
        }

        this.file = null;
        this.refetchDocuments = true;

        if (closeAfter) {
          this.$emit("onModify", { refetchDocuments: this.refetchDocuments });
          return;
        }

        this.showAlertSuccess();
        await this.initializeFormGroupSubmitter();
      } else {
        this.scrolltoErrors();
      }
    },
    setStatus(newStatus) {
      this.status = newStatus;
      this.setFormSubmitterFormHasUnsavedChanges({ hasUnsavedChanges: true });
    },
    closeModal() {
      if (this.hasUnsavedChanges) {
        this.$dialog
          .confirm(
            {
              title: "Do you want to save your changes?"
            },
            {
              view: "confirm-unsaved-changes"
            }
          )
          .then(async ({ data }) => {
            if (data.confirmValue) {
              this.prevSubmitForm(true);
            } else {
              this.$emit("onModify", {
                refetchDocuments: this.refetchDocuments
              });
            }
          })
          .catch(function() {});
      } else {
        this.$emit("onModify", { refetchDocuments: this.refetchDocuments });
      }
    }
  },
  computed: {
    ...mapState("formSubmitter", {
      json: state => state.form.json,
      hasUnsavedChanges: state => state.form.hasUnsavedChanges
    }),
    ...mapGetters("formGroupSubmitter", ["getFormGroupSubmitById"]),
    ...mapGetters("formGroup", ["getFormGroupById"]),
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    modalTitle() {
      return this.formGroup ? this.formGroup.name : "";
    },
    modalSubtitle() {
      let title = "";
      if (this.eoc) {
        title = this.formatEoc(
          " | " + this.eoc.patient.firstName,
          this.eoc.patient.middleName,
          this.eoc.patient.preferredName,
          this.eoc.patient.lastName,
          this.eoc.patient.mrn,
          this.eoc.eocn
        );
      }
      return title;
    },
    statusClass() {
      if (this.status) {
        switch (this.status) {
          case "Pending":
            return "badge-warning text-white";
          case "Completed":
            return "badge-success text-white";
          case "Draft":
            return "badge-danger";
          default:
            break;
        }
      }
      return "light";
    },
    statusIcon() {
      if (this.status) {
        switch (this.status) {
          case "Pending":
            return "fa-clock";
          case "Completed":
            return "fa-check-circle";
          case "Draft":
            return "fa-clock";
          default:
            break;
        }
      }
      return "light";
    },
    statuses() {
      return [
        {
          value: "Draft",
          label: "Draft",
          class: "bg-danger",
          disabled: this.submit && this.submit.status == "Completed"
        },
        {
          value: "Pending",
          label: "Pending",
          class: "bg-warning",
          disabled: this.submit && this.submit.status == "Completed"
        },
        {
          value: "Completed",
          label: "Completed",
          class: "bg-success",
          disabled: !this.hasCurrentUserDocumentPermission(
            this.formId,
            "hasCompleteAccess"
          )
        }
      ];
    }
  },
  watch: {
    effectiveDateTime() {
      this.setFormSubmitterFormHasUnsavedChanges({ hasUnsavedChanges: true });
    }
  }
};
</script>

<style scoped>
.status-badge {
  font-size: 16px;
  padding: 10px 18px !important;
}

.overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  background: rgba(255, 255, 255, 0.5);
  z-index: -1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.overlay.show {
  z-index: 100;
}
</style>
